import CohortMultiSelect, {
  resolveCohortIds,
} from '@/components/cohorts/cohort-multi-select';
import { useCohorts } from '@/hooks/queries/cohorts';
import { useBulkAssignUserCohorts } from '@/hooks/queries/users';
import { User } from '@/types/user';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Selection,
} from '@nextui-org/react';
import { useMemo, useState } from 'react';

interface BulkCohortAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: User[];
  onSuccess?: () => void;
}

export default function BulkCohortAssignmentModal({
  isOpen,
  onClose,
  selectedUsers,
  onSuccess,
}: BulkCohortAssignmentModalProps) {
  const [selectedCohortIds, setSelectedCohortIds] = useState<Selection>(
    new Set([])
  );
  const [showCohortError, setShowCohortError] = useState(false);

  const { data: cohorts } = useCohorts();

  const selectedCohortIdList = useMemo(
    () => resolveCohortIds(selectedCohortIds, cohorts?.results ?? []),
    [selectedCohortIds, cohorts?.results]
  );

  const hasSelectedCohorts = selectedCohortIdList.length > 0;

  const { mutate: bulkAssignCohorts, isPending } = useBulkAssignUserCohorts({
    onSuccess: () => {
      setSelectedCohortIds(new Set([]));
      setShowCohortError(false);
      onSuccess?.();
      onClose();
    },
    successMessage: `Cohorts assigned to ${selectedUsers.length} user${
      selectedUsers.length === 1 ? '' : 's'
    }`,
  });

  const handleClose = () => {
    setSelectedCohortIds(new Set([]));
    setShowCohortError(false);
    onClose();
  };

  const handleAssign = () => {
    setShowCohortError(true);

    if (!hasSelectedCohorts || selectedUsers.length === 0) {
      return;
    }

    bulkAssignCohorts(
      selectedUsers.map((user) => ({
        user_id: user.id,
        cohorts: selectedCohortIdList,
      }))
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" placement="center">
      <ModalContent>
        <ModalHeader>Assign Cohorts</ModalHeader>
        <ModalBody className="gap-4">
          <p className="text-sm text-default-500">
            Assign cohorts to {selectedUsers.length} selected user
            {selectedUsers.length === 1 ? '' : 's'}. This replaces each
            user&apos;s current cohort assignments.
          </p>
          <CohortMultiSelect
            selectedKeys={selectedCohortIds}
            onSelectionChange={setSelectedCohortIds}
            isRequired
            isInvalid={showCohortError && !hasSelectedCohorts}
            errorMessage={
              showCohortError && !hasSelectedCohorts
                ? 'At least one cohort is required'
                : undefined
            }
          />
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleAssign}
            isLoading={isPending}
            isDisabled={selectedUsers.length === 0}
          >
            Assign Cohorts
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
